/* eslint-disable no-nested-ternary */
import { Col, Image, message, Row, Select, Spin } from "antd";
import {
  Button,
  ColorAntdButton,
  CustomAntdModal,
  CustomAntdTable,
  SearchElement,
  StyledButton,
  StyledInput
} from "common/components/StyledElements";
import APIConstants from "common/constants/APIConstants";
import COLORS from "common/constants/Colors";
import { useAuthContext } from "common/contexts/AuthContext";
import { api } from "common/utils/APIMethods";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQueries, useQuery } from "react-query";
import styled from "styled-components";
import NewNotification from "./NewNotification";

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
const ActionButton = styled(Button)`
  height: 35px;
  margin-bottom: 15px;
  margin-left: 20px;
`;

const NewAntdTable = styled(CustomAntdTable)`
  margin: 30px;
  width: 95%;
`;
const FormItemInner = styled.div`
  display: grid;
  grid-template-columns: 180px 0.6fr;
  margin-bottom: 30px;
`;

const BoldText = styled.div`
  color: rgba(0, 87, 84, 0.8);
  font-family: Poppins;
  font-size: 15px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  white-space: nowrap;
`;

const StyledController = styled.div`
  position: relative;
  width: 70%;
`;

const ModalInput = styled(StyledInput)`
  width: 100%;
  color: #2e2e2e;
`;

const ErrorText = styled.span`
  position: absolute;
  top: 100%;
  left: 0;
  color: red;
  font-size: 12px;
  margin-top: 5px;
`;

const Container = styled.div`
  position: fixed;
`;

const InputFileLabel = styled.label`
  border: 1px solid ${COLORS.primary_green};
  background-color: ${COLORS.primary_green};
  border-radius: 5px;
  padding: 10px;
  cursor: pointer;
  color: white;
  font-weight: bold;
`;
const FormCol = styled(Col)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const StyledDeleteButton = styled(Button)`
  color: white;
  border: red;
`;

const InputElement = styled.input`
  height: 50px;
  border: 1.5px solid ${COLORS.primary_green};
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 10px;
  outline: none;
`;

const UploadBanner = styled(Image)`
  height: 68px;
  width: 68px;
  margin-left: 40px;
  border: 1.5px solid ${COLORS.primary_green};
  border-radius: 6px;
  margin-right: 30px;
`;

const UpdateBannerModal = (props) => {
  const [stateIds, setStateIds] = useState([]);
  const { authConfig } = useAuthContext();
  const imageRef = useRef(null);

  const ROLE_TYPE = [
    {
      title: "retailer",
      value: "ROLE_RETAILER",
      id: 2
    },
    {
      title: "distributor",
      value: "ROLE_DISTRIBUTOR",
      id: 1
    }
  ];
  const LEVEL_TYPE = [
    {
      title: "One",
      value: "LEVEL_ONE"
    },
    {
      title: "Two",
      value: "LEVEL_TWO"
    },
    {
      title: "Three",
      value: "LEVEL_THREE"
    }
  ];

  const DIRECTION = ["internal", "external"];
  const PRODUCT_TYPE = ["credit", "product"];

  const {
    selectedBanner,
    stateList,
    handleAddBanners,
    handleUpdateBanners,
    isLoading
  } = props;
  const { allDistricts } = APIConstants;
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors }
  } = useForm(
    selectedBanner
      ? {
          defaultValues: {
            id: selectedBanner.id,
            redirectLink: selectedBanner.redirectLink,
            bannerType: LEVEL_TYPE.find(
              (item) => item.title === selectedBanner.bannerType
            )?.value,
            role: [selectedBanner.role.id],
            productType: selectedBanner?.product || null,
            direction: selectedBanner.direction,
            banner: selectedBanner.mobileBannerLink,
            district: selectedBanner?.districts?.map((item) => item.id),
            state: [
              ...new Set(
                selectedBanner?.districts?.map(
                  (district) => district?.state?.id
                )
              )
            ]
          }
        }
      : {}
  );

  useEffect(() => {
    if (watch("state") !== null) {
      setStateIds([
        ...new Set(
          selectedBanner?.districts?.map((district) => district?.state?.id)
        )
      ]);
    }
  }, []);

  const { Option, OptGroup } = Select;

  const districtData = useQueries(
    stateIds?.map((stateId) => ({
      queryKey: ["allDistrictList", stateId],
      queryFn: () =>
        api(
          {
            url: `${allDistricts}/${stateId}`
          },
          authConfig
        ),
      staleTime: Infinity,
      enabled: stateId !== undefined
    }))
  );
  const isDistrictsLoading = districtData.some(
    (query) => query.status === "loading"
  );
  const stateDistrictMap = new Map();
  districtData.forEach((item) => {
    stateDistrictMap.set(item?.data?.[0]?.state, item?.data);
  });

  const onSubmit = (data) => {
    const formData = new FormData();

    const bannerDetails = {
      ...(selectedBanner && { id: data.id }),
      bannerType: data.bannerType,
      redirectLink: data.redirectLink,
      product: data.productType,
      direction: data.direction
    };

    formData.append("roleIds", JSON.stringify(data.role));
    formData.append("bannerDetails", JSON.stringify(bannerDetails));
    formData.append("stateIds", JSON.stringify(data.state || []));
    formData.append("districtIds", JSON.stringify(data.district || []));

    formData.append("bannerImage", imageRef.current);

    if (!selectedBanner) {
      handleAddBanners(formData);
    } else {
      handleUpdateBanners(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormContainer>
        <Row gutter={24}>
          <FormCol span={12}>
            <FormItemInner>
              <BoldText>Banner Type</BoldText>
              <Controller
                name="bannerType"
                control={control}
                rules={{ required: "Select Banner Type" }}
                render={({ field }) => (
                  <StyledController>
                    <Select
                      placeholder="Select Banner Type"
                      size="large"
                      style={{ width: 210 }}
                      onChange={(value) => field.onChange(value)}
                      value={field.value}
                    >
                      {LEVEL_TYPE.map((item) => (
                        <Option key={item.value} value={item.value}>
                          {item.title}
                        </Option>
                      ))}
                    </Select>
                    {errors?.bannerType && (
                      <p style={{ color: "red" }}>
                        {errors.bannerType.message}
                      </p>
                    )}
                  </StyledController>
                )}
              />
            </FormItemInner>
          </FormCol>
          <FormCol span={12}>
            <FormItemInner>
              <BoldText>Display For</BoldText>
              <Controller
                name="role"
                control={control}
                rules={{ required: "select role" }}
                render={({ field }) => (
                  <StyledController>
                    <Select
                      mode="multiple"
                      placeholder="Select display for"
                      size="large"
                      style={{ width: 210 }}
                      onChange={(value) => field.onChange(value)}
                      value={field.value}
                    >
                      {ROLE_TYPE.map((item) => (
                        <Option key={item.id} value={item.id}>
                          {item.title}
                        </Option>
                      ))}
                    </Select>
                    {errors?.role && (
                      <p style={{ color: "red" }}>{errors.role.message}</p>
                    )}
                  </StyledController>
                )}
              />
            </FormItemInner>
          </FormCol>
        </Row>

        <Row gutter={24}>
          <FormCol span={12}>
            <FormItemInner>
              <BoldText>Direction</BoldText>
              <Controller
                name="direction"
                control={control}
                rules={{ required: "select direction" }}
                render={({ field }) => (
                  <StyledController>
                    <Select
                      placeholder="Select direction"
                      size="large"
                      style={{ width: 210 }}
                      onChange={(value) => field.onChange(value)}
                      value={field.value}
                    >
                      {DIRECTION.map((item) => (
                        <Option key={item} value={item}>
                          {item}
                        </Option>
                      ))}
                    </Select>
                    {errors?.direction && (
                      <p style={{ color: "red" }}>{errors.direction.message}</p>
                    )}
                  </StyledController>
                )}
              />
            </FormItemInner>
          </FormCol>
          <FormCol span={12}>
            <FormItemInner>
              <BoldText>Credit/Product</BoldText>
              <Controller
                name="productType"
                control={control}
                render={({ field }) => (
                  <StyledController>
                    <Select
                      placeholder="Select Credit/Product"
                      size="large"
                      style={{ width: 210 }}
                      onChange={(value) => field.onChange(value)}
                      value={field.value}
                      allowClear
                    >
                      {PRODUCT_TYPE.map((item) => (
                        <Option key={item} value={item}>
                          {item}
                        </Option>
                      ))}
                    </Select>
                  </StyledController>
                )}
              />
            </FormItemInner>
          </FormCol>
        </Row>
        {watch("productType") && (
          <FormItemInner>
            <BoldText>
              {watch("productType") === "credit"
                ? "Redirect Link"
                : "product Name"}
            </BoldText>
            <Controller
              name="redirectLink"
              control={control}
              render={({ field }) => (
                <StyledController>
                  <ModalInput
                    style={{ width: 740 }}
                    {...field}
                    placeholder={
                      watch("productType") === "credit"
                        ? "Redirect Link"
                        : "product Name"
                    }
                    autoFocus
                  />
                  {errors.productName && (
                    <ErrorText>{errors.productName.message}</ErrorText>
                  )}
                </StyledController>
              )}
            />
          </FormItemInner>
        )}
        <FormItemInner>
          <BoldText>Select State</BoldText>
          <Controller
            name="state"
            control={control}
            render={({ field }) => (
              <StyledController>
                <Select
                  placeholder="Select State"
                  mode="multiple"
                  size="large"
                  listHeight={150}
                  style={{ width: 740 }}
                  optionFilterProp="key"
                  onChange={(value) => {
                    if (!value) {
                      field.onChange([]);
                      setValue("district", []);
                    } else {
                      field.onChange(value);
                      setStateIds(value);
                      const tempMap = new Map(stateDistrictMap);
                      for (const key of tempMap.keys()) {
                        if (!value.includes(key.id)) {
                          tempMap.delete(key);
                        }
                      }
                      const filteredDistricts = Array.from(tempMap.values())
                        .flat()
                        .map((district) =>
                          watch("district").includes(district.id)
                            ? district.id
                            : null
                        )
                        .filter((id) => id !== null);

                      setValue("district", filteredDistricts);
                    }
                  }}
                  value={field.value}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  maxTagCount="responsive"
                  allowClear
                  placement="bottomLeft"
                >
                  {stateList?.map((item) => (
                    <Option key={item.name} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </StyledController>
            )}
          />
        </FormItemInner>
        <FormItemInner>
          <BoldText>Select District</BoldText>
          <Controller
            name="district"
            control={control}
            render={({ field }) => (
              <StyledController>
                <Select
                  loading={isDistrictsLoading}
                  placeholder="Select District"
                  mode="multiple"
                  size="large"
                  style={{ width: 740 }}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                  listHeight={150}
                  value={field.value}
                  optionFilterProp="key"
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  maxTagCount="responsive"
                  allowClear
                  popupMatchSelectWidth={false}
                  placement="bottomLeft"
                >
                  {Array.from(stateDistrictMap.entries())?.map(
                    ([stateName, districts]) => (
                      <OptGroup key={stateName?.id} label={stateName?.name}>
                        {districts?.map((item) => (
                          <Option key={item.name} value={item.id}>
                            {item.name}
                          </Option>
                        ))}
                      </OptGroup>
                    )
                  )}
                </Select>
              </StyledController>
            )}
          />
        </FormItemInner>
        <FormItemInner>
          <BoldText>Upload Banner</BoldText>
          <Controller
            name="banner"
            control={control}
            rules={{ required: "Upload Banner" }}
            render={({ field }) => (
              <StyledController>
                <InputFileLabel htmlFor="imageUpload">
                  Upload File
                </InputFileLabel>
                <InputElement
                  id="imageUpload"
                  type="file"
                  style={{ display: "none" }}
                  accept="image/*"
                  onChange={(e) => {
                    imageRef.current = e.target?.files[0];

                    setValue("banner", URL.createObjectURL(e.target?.files[0]));
                  }}
                />
                {errors.banner && (
                  <ErrorText>{errors.banner.message}</ErrorText>
                )}

                {field.value && (
                  <>
                    <UploadBanner src={field.value} />
                    <ColorAntdButton
                      color={COLORS.action_decline}
                      onClick={() => {
                        setValue("banner", null);
                      }}
                    >
                      Remove File
                    </ColorAntdButton>
                  </>
                )}
              </StyledController>
            )}
          />
        </FormItemInner>
        <div
          style={{ display: "flex", justifyContent: "center", marginTop: 16 }}
        >
          <StyledButton type="primary" htmlType="submit">
            Submit {isLoading && <Spin />}
          </StyledButton>
        </div>
      </FormContainer>
    </form>
  );
};

const AllBanners = () => {
  const [isUpdateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [bannerImage, setBannerImage] = useState(null);
  const [updateBannerImage, setUpdateBannerImage] = useState(null);
  const { allBanners, allStates, allDistricts, updateBanner } = APIConstants;
  const { authConfig } = useAuthContext();
  const [searchText, setSearchText] = useState("");
  const [stateIds, setStateIds] = useState([]);
  const [isDeleteClicked, setDeleteClicked] = useState(false);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const { Option, OptGroup } = Select;
  const [isNewNotificationModalOpen, setIsNewNotificationModalOpen] = useState(false);

  const fetchAllBanners = () =>
    api(
      {
        url: allBanners,
        method: "GET"
      },
      authConfig
    );

  const {
    data: bannersData,
    isLoading: isBannersLoading,
    refetch: refetchBannersData
  } = useQuery("all", fetchAllBanners);

  const { data: stateList = [], isLoading: isStateLoading } = useQuery(
    ["allStates"],
    () =>
      api(
        {
          url: allStates
        },
        authConfig
      ),
    {
      staleTime: Infinity,
      onError: (error) => {
        message.error(error);
      }
    }
  );

  const districtList = useQueries(
    stateIds?.map((stateId) => ({
      queryKey: ["allDistrictList", stateId],
      queryFn: () =>
        api(
          {
            url: `${allDistricts}/${stateId}`
          },
          authConfig
        ),
      staleTime: Infinity,
      enabled: stateId !== undefined
    }))
  );
  const stateDistrictMap = new Map();
  const isDistrictsLoading = districtList.some(
    (query) => query.status === "loading"
  );

  districtList.forEach((item) => {
    stateDistrictMap.set(item?.data?.[0]?.state, item?.data);
  });

  const { mutate: mutateUpdateBanners, isLoading: isUpdating } = useMutation(
    (bannerData) =>
      api(
        {
          url: updateBanner,
          method: "PUT",
          body: bannerData,
          isFormData: true
        },
        authConfig
      ),
    {
      onSuccess: (response) => {
        refetchBannersData();
        message.success(response.message ?? "Banner is updated");
        setSelectedBanner(null);
        setUpdateModalOpen(false);
      },
      onError: (error) => {
        message.error(error.message ?? "Banner is not updated");
      }
    }
  );

  const { mutate: mutateAddBanners, isLoading: isAdding } = useMutation(
    (bannerData) =>
      api(
        {
          url: updateBanner,
          method: "POST",
          body: bannerData,
          isFormData: true
        },
        authConfig
      ),
    {
      onSuccess: () => {
        refetchBannersData();
        message.success("Banner is Added");
        setSelectedBanner(null);
        setUpdateModalOpen(false);
      },
      onError: (error) => {
        message.error(error.message ?? "Banner is not added");
      }
    }
  );

  const { mutate: mutateDeleteBanner, isLoading: isDeleting } = useMutation(
    (bannerIds) =>
      api(
        {
          url: updateBanner,
          method: "DELETE",
          body: bannerIds
        },
        authConfig
      ),
    {
      onSuccess: (response) => {
        refetchBannersData();
        setSelectedBanner(null);
        setDeleteClicked(false);
        message.success(response.message ?? "Banner is Deleted");
      },
      onError: (error) => {
        message.error(error.message ?? "Banner is not deleted");
      }
    }
  );

  const allBannersData = bannersData?.map((item) => {
    const BannerName = item.mobileBannerLink
      .substring(
        item?.mobileBannerLink.includes("+")
          ? item.mobileBannerLink.lastIndexOf("+") + 1
          : item.mobileBannerLink.lastIndexOf("/") + 1
      )
      .split(".")[0];
    return {
      ...item,
      bannerName: BannerName
    };
  });

  const tableData = allBannersData?.map((bannerDetails) => {
    const {
      id,
      bannerType = "",
      redirectLink = "-",
      direction = "internal",
      mobileBannerLink = null,
      webBannerLink = null,
      role = null,
      bannerName,
      product,
      districts,
      state
    } = bannerDetails;

    return {
      id,
      bannerType,
      redirectLink,
      direction,
      mobileBannerLink,
      webBannerLink,
      role,
      bannerName,
      product,
      districts,
      state
    };
  });

  const filteredBanners = useMemo(
    () =>
      tableData?.filter((item) => {
        if (searchText.length > 0) {
          const hasBannerName = item?.bannerName
            .toUpperCase()
            .includes(searchText.toUpperCase());
          if (!hasBannerName) {
            return false;
          }
        }

        if (stateIds.length > 0) {
          const hasStateName = item?.districts?.some((district) =>
            stateIds.includes(district.state.id)
          );
          if (!hasStateName) {
            return false;
          }
        }

        if (selectedDistricts.length > 0) {
          const hasDistrictName = item?.districts?.some((district) =>
            selectedDistricts.includes(district.id)
          );
          if (!hasDistrictName) {
            return false;
          }
        }
        return true;
      }),
    [stateIds, selectedDistricts, searchText, tableData]
  );

  const columns = [
    {
      title: "Banner Name",
      dataIndex: "bannerName",
      key: "bannerName"
    },
    {
      title: "Banner Type",
      dataIndex: "bannerType",
      key: "bannerType",
      filters: [
        {
          text: "Level One",
          value: "One"
        },
        {
          text: "Level Two",
          value: "Two"
        },
        {
          text: "Level Three",
          value: "Three"
        }
      ],
      onFilter: (value, record) => record.bannerType === value
    },
    {
      title: "redirect link",
      dataIndex: "redirectLink",
      key: "redirectLink",
      render: (redirectLink) => (redirectLink === null ? "-" : redirectLink)
    },
    {
      title: "Display For",
      dataIndex: "role",
      render: (role) => (role.id === 1 ? "Distributor" : "Retailer")
    },
    {
      title: "Credit/Product",
      render: (action) =>
        action.product
          ? action.product === "credit"
            ? "Credit"
            : "Product"
          : "-"
    },
    {
      title: "direction",
      dataIndex: "direction",
      key: "direction"
    },
    {
      title: "Banner Image",
      dataIndex: "mobileBannerLink",
      render: (mobileBannerLink) => (
        <Image src={mobileBannerLink} alt="mobileBannerLink" />
      )
    },
    {
      title: "Update",
      render: (bannerDetails) => (
        <Button
          onClick={() => {
            setSelectedBanner(bannerDetails);
            setUpdateModalOpen(true);
          }}
        >
          Update
        </Button>
      )
    },
    {
      title: "Delete",
      render: (bannerDetails) => (
        <StyledDeleteButton
          type="danger"
          onClick={() => {
            setSelectedBanner(bannerDetails);
            setDeleteClicked(true);
          }}
        >
          Delete
        </StyledDeleteButton>
      )
    }
  ];

  const updateProductModelProps = {
    centered: true,
    title: selectedBanner ? "Update Banner" : "Add Banner",
    width: "70%",
    footer: null,
    height: "80%",
    destroyOnClose: true
  };
  const deleteBannerModelProps = {
    centered: true,
    title: "Delete Banner",
    footer: null,
    height: "80%",
    destroyOnClose: true
  };

  return (
    <Container>
      <SearchElement
        placeholder="Search Banner Name"
        onChange={(e) => setSearchText(e.target.value)}
        enterButton
        style={{ marginLeft: "30px", width: "30%" }}
        size="large"
      />
      <Select
        placeholder="Select State"
        mode="multiple"
        loading={isStateLoading}
        size="large"
        listHeight={150}
        style={{ width: 250, marginLeft: "20px" }}
        onChange={(value) => {
          setStateIds(value);

          const tempMap = new Map(stateDistrictMap);

          for (const key of tempMap.keys()) {
            if (!value.includes(key.id)) {
              tempMap.delete(key);
            }
          }
          const filteredDistricts = Array.from(tempMap.values())
            .flat()
            .map((district) =>
              selectedDistricts.includes(district.id) ? district.id : null
            )
            .filter((id) => id !== null);
          setSelectedDistricts(filteredDistricts);
        }}
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
        maxTagCount="responsive"
        allowClear
        placement="bottomLeft"
        optionFilterProp="key"
      >
        {stateList?.map((item) => (
          <Option key={item.name} value={item.id}>
            {item.name}
          </Option>
        ))}
      </Select>

      <Select
        loading={isDistrictsLoading}
        placeholder="Select District"
        mode="multiple"
        size="large"
        style={{ width: 250, marginLeft: "20px" }}
        onChange={(value) => {
          setSelectedDistricts(value);
        }}
        value={selectedDistricts}
        listHeight={150}
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
        maxTagCount="responsive"
        allowClear
        optionFilterProp="key"
        popupMatchSelectWidth={false}
        placement="bottomLeft"
      >
        {Array.from(stateDistrictMap.entries())?.map(
          ([stateName, districts]) => (
            <OptGroup key={stateName?.id} label={stateName?.name}>
              {districts?.map((item) => (
                <Option key={item.name} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </OptGroup>
          )
        )}
      </Select>

      <ActionButton primary="true" onClick={() => setUpdateModalOpen(true)}>
        Add Banner
      </ActionButton>
      <ActionButton
        primary="true"
        onClick={() => setIsNewNotificationModalOpen(true)}
      >
        Send Notification
      </ActionButton>

      <NewAntdTable
        loading={isBannersLoading}
        columns={columns}
        scroll={{ y: "calc(60vh)" }}
        dataSource={filteredBanners}
        size="large"
      />

      <CustomAntdModal
        {...updateProductModelProps}
        open={isNewNotificationModalOpen}
        title="Send Notification"
        onCancel={() => {
          setIsNewNotificationModalOpen(false);
        }}
      >
        <NewNotification stateList={stateList}
        setIsNewNotificationModalOpen={setIsNewNotificationModalOpen}
        />
      </CustomAntdModal>

      <CustomAntdModal
        open={!!bannerImage}
        title="Image Preview"
        centered
        onCancel={() => setBannerImage(null)}
        footer={null}
        width="60%"
      >
        <embed
          src={bannerImage}
          width="100%"
          height="500"
          type="application/pdf"
        />
      </CustomAntdModal>
      <CustomAntdModal
        open={isUpdateModalOpen}
        onCancel={() => {
          setUpdateModalOpen(false);
          setSelectedBanner(null);
        }}
        {...updateProductModelProps}
      >
        <UpdateBannerModal
          selectedBanner={selectedBanner}
          setUpdateBannerImage={setUpdateBannerImage}
          updateBannerImage={updateBannerImage}
          stateList={stateList}
          handleUpdateBanners={mutateUpdateBanners}
          handleAddBanners={mutateAddBanners}
          isLoading={isAdding || isUpdating}
        />
      </CustomAntdModal>

      <CustomAntdModal
        open={isDeleteClicked}
        onCancel={() => {
          setDeleteClicked(false);
          setSelectedBanner(null);
        }}
        {...deleteBannerModelProps}
        footer={[
          <Button
            onClick={() => {
              setDeleteClicked(false);
              setSelectedBanner(null);
            }}
          >
            CANCEL
          </Button>,
          <StyledDeleteButton
            type="danger"
            onClick={() => {
              mutateDeleteBanner([selectedBanner.id]);
            }}
          >
            Delete {isDeleting && <Spin />}
          </StyledDeleteButton>
        ]}
      >
        <Image src={selectedBanner?.webBannerLink} />
      </CustomAntdModal>
    </Container>
  );
};

export default AllBanners;
